import dayjs from 'dayjs'
import { attrController } from '~/apis/back/services'
import type {
  AttProductGroupDto,
  AttProductGroupItemDto,
  ResponseGetAttrProductsResponse,
  ResponseQueryAttrDetailResponse,
  ResponseQueryAttrInfoResponse,
} from '~/apis/back/types'
import { BACKEND_PREFIX } from '~/constant/request'
import { ENUM_EventType, ENUM_PageName, E_CATEGORY_TYPE } from '~/enums'
import type { BookItemClickEvent, ViewItemViewEvent } from '~/types/event-tracking'

interface IFormatedProductItem extends AttProductGroupDto {
  showMore: boolean
  needExpand: boolean
  items?: Array<AttProductGroupItemDto & {
    tags: string[]
    travellerType: string
  }>
}

export function useAttractionDetail() {
  const detailState = useState('azgo_attraction_detail', () => ({
    groupedProducts: {} as ResponseGetAttrProductsResponse,
    attrInfo: {} as ResponseQueryAttrInfoResponse,
    attrDetail: {} as ResponseQueryAttrDetailResponse,
    formattedProducts: [] as IFormatedProductItem[],
    isError: false,
    showPopup: false,
    loading: false,
    currentFocusOption: '',
    isScrollByUser: true,
  }))

  const isMobile = useMobileByScreen()
  const userStore = useUserStore()
  const { getRequestFilterConditions, filterConditions, getOptionsByCodes } = useFilterConditionsStore()
  const route = useRoute()
  const { locale } = useLocale()
  const gtm = useGtm()
  const { t } = useI18n()
  const attrId = computed(() => {
    const detail = route.params.detail as string
    if (!detail) {
      return Number(route.query.attrId as string)
    }
    const attrIdStr = detail.split('-').pop()
    return Number(attrIdStr)
  })

  const {
    groupedProducts,
    attrInfo,
    attrDetail,
    formattedProducts,
    showPopup,
    isError,
    loading,
    currentFocusOption,
    isScrollByUser,
  } = toRefs(detailState.value)

  const { jumpLoading, jumpBrandUrl } = useSearchResult()

  const { $fetch } = useNuxtApp()

  const expandLength = computed(() => 3)

  const attrBasicInfo = computed(() => attrInfo.value.data)

  const topHeight = computed(() => isMobile.value ? 80 : 139)

  function scrollListener() {
    const ids = formattedProducts.value.map(group => group.groupName!)
    if (!isScrollByUser.value) {
      isScrollByUser.value = true
      return
    }
    for (const id of ids) {
      const el = document.getElementById(id)
      if (el && isElInViewport(el)) {
        currentFocusOption.value = id
        return
      }
    }
    currentFocusOption.value = ''
  }

  function isElInViewport(el: HTMLElement) {
    const rect = el.getBoundingClientRect()
    // return rect.top >= 0 && rect.left >= 0 && rect.bottom <= window.innerHeight && rect.right <= window.innerWidth
    const { top, bottom } = rect
    return (top < 1 / 2 * window.innerHeight && bottom > topHeight.value)
  }

  const debounceScrollListener = debounce(scrollListener, 50)

  function addScrollListener() {
    document.querySelector('#scroll-container')?.addEventListener('scroll', debounceScrollListener)
  }

  function removeScrollListener() {
    document.querySelector('#scroll-container')?.removeEventListener('scroll', debounceScrollListener)
  }

  async function fetchUrl(item: AttProductGroupItemDto) {
    const result = await attrController.attrQueryVisitOutUrlPost({
      attrId: attrId.value,
      supplierId: item.supplierId,
      productId: item?.productId,
      price: item.price,
      searchDate: getRequestFilterConditions().date,
      distCode: userStore.userInfo.distCode,
      cid: route.query.cid as string,
      memberId: userStore.userInfo.memberId,
      path: route.fullPath,
      productCode: item.productCode,
      productSkuCode: item.productSkuCode,
      currency: userStore.userInfo.currency,
      // eslint-disable-next-line ts/ban-ts-comment
      // @ts-ignore
      supplierAttId: item.supplierAttId as string,
    })

    return result.data.value?.data?.jumpUrl || ''
  }

  function watchMobile() {
    watch(isMobile, () => {
      formattedProducts.value.forEach((group) => {
        group.needExpand = (group.items?.length || 0) > expandLength.value
      })
    })
  }

  function trackDetailViewDealEvent(isBigBtn = false, jumpUrl: string, item: AttProductGroupDto, rankIndex: number) {
    const skuItem = item.items?.[rankIndex] || {}
    const selectItemParams: BookItemClickEvent = {
      event: ENUM_EventType.BookItem,
      page_category: ENUM_PageName.Detail,
      city_id: attrBasicInfo.value?.cityId,
      city: attrBasicInfo.value?.cityName,
      country_code: route.params.countryCode as string,
      locale: locale.value,
      search_date: dayjs(filterConditions.date).format('YYYY-MM-DD'),
      campaign_id: route.query.campaign_id as string ?? '',
      campaign_name: route.query.campaign_name as string ?? '',
      currency: userStore.userInfo.currency,
      attraction_id: attrId.value,
      attraction_name: attrBasicInfo.value?.attrName,
      supplier_id: String(skuItem?.supplierId),
      supplier_name: skuItem?.supplierName,
      product_id: skuItem.productId,
      product_name: skuItem.productName ?? '',
      product_list_position: !isBigBtn ? (rankIndex ?? 0) + 1 : 0,
      product_price: skuItem?.price,
      click_url: jumpUrl,
      product_type: item.groupId,
      product_att_type: item.groupId,
    }
    gtm?.trackEvent(selectItemParams)
  }

  const { state: priceCalendarState } = usePriceCalendarState()

  async function clickViewDeal(item: AttProductGroupDto, rankIndex: number) {
    const skuItem = item.items?.[rankIndex] || {}
    const isBigBtn = false
    if (skuItem.bookingDirect) {
      priceCalendarState.value.params = {
        supplierId: skuItem?.supplierId?.toString() || '',
        productId: skuItem.productCode ?? '',
        productSkuId: skuItem?.productSkuCode ?? '',
        supplierAttId: skuItem.supplierAttId?.toString() || '',
        skuName: skuItem.showName,
      }
      priceCalendarState.value.isShow = true
      trackDetailViewDealEvent(isBigBtn, '', item, rankIndex)
      return
    }

    if (skuItem) {
      jumpLoading.value = true
      jumpBrandUrl.value = skuItem.logoUrl || ''
      const jumpUrl = await fetchUrl(skuItem)
      setTimeout(() => {
        jumpLoading.value = false
        jumpBrandUrl.value = ''
      }, 3000)
      if (jumpUrl) {
        trackDetailViewDealEvent(isBigBtn, jumpUrl, item, rankIndex)

        return navigateTo(jumpUrl, {
          external: true,
          open: isMobile.value
            ? undefined
            : {
                target: '_blank',
              },
        })
      }
      jumpLoading.value = false
      jumpBrandUrl.value = ''
      showToast(t('key.price.calendar.request.failed'))
    }
  }

  async function getAttrProducts(): Promise<boolean> {
    try {
      loading.value = true
      const data: ResponseGetAttrProductsResponse = await $fetch<ResponseGetAttrProductsResponse>(`${BACKEND_PREFIX}/attr/list/groupedProduct`, {
        method: 'post',
        body: {
          attrId: attrId.value,
          date: getRequestFilterConditions().detailDate,
          currency: userStore.userInfo.currency,
          filters: getOptionsByCodes(true),
        },
      })
      formattedProducts.value = data?.data?.attProductGroupDto?.map(group => ({
        ...group,
        showMore: false,
        needExpand: (group.items?.length || 0) > expandLength.value,
        items: group.items?.sort((a, b) => {
          if (a.ageBand === b.ageBand) {
            return (a.price || Infinity) - (b.price || Infinity)
          }
          return (a.ageBand || 1) - (b.ageBand || 1)
        }).map(item => ({
          ...item,
          logoUrl: item.logoUrl?.replace('http://', 'https://') || '',
          tags: [
            item.freeCancellation === 1 && t('key.experience.filter.free.cancellation'),
            item.instantConfirmation === 1 && t('key.experience.filter.instant.confirmation'),
          ]
            .filter(Boolean) as string[],
          travellerType: 'Adult',
        })),
      })) || []
      groupedProducts.value = data
      console.log(formattedProducts.value)
      return true
    }
    catch (e) {
      console.error(e)
      formattedProducts.value = []
      return false
    }
    finally {
      loading.value = false
    }
  }

  async function getAttrDesc() {
    const { data, error } = await attrController.attrDetailPost({ attrId: attrId.value })
    if (error.value) {
      isError.value = true
      return
    }
    attrDetail.value = data.value!
  }

  async function getAttrInfo() {
    const { data, error } = await attrController.attrInfoPost({ attrId: attrId.value })
    if (error.value) {
      isError.value = true
      return
    }
    attrInfo.value = data.value!
  }

  async function getAttrResponse() {
    await Promise.all([getAttrDesc(), getAttrInfo()])
  }

  function getPrice(item: AttProductGroupItemDto) {
    if (!item.price) {
      return ''
    }
    return `${getCurrencySymbol(item.currency)}${toThousands(item.price)}`
  }

  function showAllOrLess(product: IFormatedProductItem) {
    product.showMore = !product.showMore
  }

  function getAttrDetail() {
    showPopup.value = true
  }

  function initFilter() {
    const filterCookie = useCookie<FilterConditions | undefined>('azgo-filter-conditions')
    filterConditions.detailFilterIdList
      = filterCookie.value?.filterIdList
      || filterConditions.defaultFilterIdList
    filterConditions.detailDate = filterCookie.value?.date || new Date().getTime()
  }

  function getExpandWord(item: AttProductGroupDto) {
    const minPriceItem = item.items?.slice()?.sort((a, b) => (a.price || Infinity) - (b.price || Infinity))[0]
    if (!minPriceItem?.price) {
      return t('key.attraction.detail.price.allOptions')
    }
    const price = `${getCurrencySymbol(minPriceItem?.currency)}${toThousands(minPriceItem?.price)}`
    const moreNum = (item.items?.length || 0) - expandLength.value
    return t('key.attractions.detail.show.more.price', [moreNum, price])
  }

  function getViewButtonText(item: AttProductGroupItemDto) {
    if (item.bookingDirect) {
      return t('key.attraction.detail.booking.direct')
    }
    return item.price ? t('key.attraction.detail.price.viewDeal') : t('key.attraction.detail.price.getPrice')
  }

  function trackViewItem() {
    const eventParams: ViewItemViewEvent = {
      event: ENUM_EventType.ViewItem,
      page_category: ENUM_PageName.Detail,
      city_id: attrBasicInfo.value?.cityId,
      city: attrBasicInfo.value?.cityName,
      locale: locale.value,
      campaign_id: route.query.campaign_id as string ?? '',
      campaign_name: route.query.campaign_name as string ?? '',
      prd_type_id: E_CATEGORY_TYPE.ATTRACTIONS,
      attraction_id: attrBasicInfo.value?.id,
      attraction_name: attrBasicInfo.value?.attrName,
      country_code: route.params.countryCode as string,
    }
    gtm?.trackEvent(eventParams)
  }
  return {
    detailState,
    groupedProducts,
    attrBasicInfo,
    attrDetail,
    formattedProducts,
    isError,
    showPopup,
    expandLength,
    loading,
    currentFocusOption,
    isScrollByUser,
    topHeight,
    attrId,
    addScrollListener,
    removeScrollListener,
    getViewButtonText,
    getExpandWord,
    initFilter,
    clickViewDeal,
    getAttrDetail,
    getPrice,
    showAllOrLess,
    getAttrResponse,
    watchMobile,
    getAttrProducts,
    trackViewItem,
  }
}
